import type {
  InstanceStatus,
  RunGroup,
  RunGroupStatus,
  RunInstance,
} from "@skaile/workspaces/types";

const ALL_STATUSES: InstanceStatus[] = [
  "queued",
  "spawning",
  "running",
  "awaiting_approval",
  "awaiting_input",
  "complete",
  "failed",
  "cancelled",
];

const TERMINAL: ReadonlySet<InstanceStatus> = new Set(["complete", "failed", "cancelled"]);

/**
 * Counters per status plus the group-status transition to apply
 * (null = none). Batch only, when all instances are terminal: any failed
 * -> `failed`; else all cancelled (no completes) -> `cancelled`; else
 * `complete` (mixed complete + cancelled counts as `complete`). `active`
 * is the reverse transition after retry_instance re-queues on a terminal
 * group - deliberately only from `complete`/`failed`: a `cancelled` group
 * never reactivates (cancel_group is a final host command; a cancelled
 * group's instances cannot be retried). Standing groups never
 * auto-transition (pause/close are host commands). Rollup owns
 * reactivation - hosts re-run it after every mutation, including retries.
 */
export function rollup(
  group: RunGroup,
  instances: RunInstance[],
): { counters: Record<InstanceStatus, number>; groupStatus: RunGroupStatus | null } {
  const counters = Object.fromEntries(ALL_STATUSES.map((s) => [s, 0])) as Record<
    InstanceStatus,
    number
  >;
  for (const i of instances) counters[i.status] += 1;

  let groupStatus: RunGroupStatus | null = null;
  if (group.mode === "batch") {
    const allTerminal =
      instances.length > 0 && instances.every((i) => TERMINAL.has(i.status));
    if (group.status === "active" && allTerminal) {
      if (counters.failed > 0) {
        groupStatus = "failed";
      } else if (counters.complete === 0 && counters.cancelled > 0) {
        groupStatus = "cancelled";
      } else {
        groupStatus = "complete";
      }
    } else if (
      (group.status === "complete" || group.status === "failed") &&
      instances.some((i) => !TERMINAL.has(i.status))
    ) {
      groupStatus = "active";
    }
  }
  return { counters, groupStatus };
}
